import Button from 'atoms/button/button';
import Heading from 'atoms/heading/heading';
import Label from 'atoms/label/label';
import ModalActions from 'atoms/modal/modalActions';
import ModalBody from 'atoms/modal/modalBody';
import ModalHeader from 'atoms/modal/modalHeader';
import ReactModal from 'atoms/modal/reactModal';
import { getDispatch } from 'reducers';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import React, { useEffect, useMemo, useState } from 'react';
import { triggerAddAccessLevel } from '../sagaActions';
import { getAutoCompleteData } from 'models/autoComplete/selectors';
import { useSelector } from 'react-redux';
import { triggerFetchAutoComplete } from 'models/autoComplete/sagaActions';
import DropdownInner, { OptionSelection, OptionType } from 'atoms/dropdown/dropdown';
import * as dynamicGridSagaActions from 'models/dynamicGrid/sagaActions';
import * as constants from './../constants';
import { Select } from 'antd';
import { RowAccessInfo } from '../rowAccess';

interface CustomToolPanelProps {
	onHide: () => void;
	rowAccessInfo?: RowAccessInfo | {};
}

export const CreateRowAccess: React.FC<CustomToolPanelProps> = (props) => {
	const { onHide } = props;
	const rowAccessInfo = props.rowAccessInfo as RowAccessInfo;
	const [user, setUser] = useState<string | undefined>(rowAccessInfo?.UserID);
	const [accessLevel, setAccessLevel] = useState<string | undefined>(rowAccessInfo?.AccessLevelID);
	const [selectedEntities, setSelectedEntities] = useState<string[] | undefined>(
		rowAccessInfo?.AccessLevelEntities?.map((a) => a?.AccessLevelEntityID)
	);

	const dispatch = getDispatch();

	const usersList = useSelector((state: any) => getAutoCompleteData(state, 'acl-all-users'));
	const accessLevels = useSelector((state: any) => getAutoCompleteData(state, 'acl-all-accessLevels'));
	const accessLevelEntities = useSelector((state: any) => getAutoCompleteData(state, 'acl-all-accessLevelEntities'));

	const accessLevelsOptions: OptionType[] = useMemo(
		() =>
			(accessLevels ?? []).map((u: any) => ({
				displayName: u.AccessLevelDisplayName,
				value: String(u.AccessLevelID),
			})),
		[accessLevels]
	);

	const selectedAccessLevel: OptionType | null = useMemo(() => {
		const found = accessLevelsOptions.find((tenant) => tenant.value === accessLevel);
		return found ? { value: found.value, displayName: found.displayName } : null;
	}, [accessLevel, accessLevelsOptions]);

	const userOptions: OptionType[] = useMemo(
		() =>
			(usersList ?? []).map((u: any) => ({
				displayName: u.UserEmail,
				value: String(u.UserID),
			})),
		[usersList]
	);

	const selectedUserEmail: OptionType | null = useMemo(() => {
		const found = userOptions.find((userDetails) => userDetails.value === user);
		return found ? { value: found.value, displayName: found.displayName } : null;
	}, [userOptions, user]);

	useEffect(() => {
		if (accessLevel) {
			dispatch(
				triggerFetchAutoComplete({
					componentId: 'acl-all-accessLevelEntities',
					options: {
						service: 'acl',
						url: 'accessLevel/entity/list',
					},
					params: { AccessLevelID: accessLevel },
				})
			);
		}
	}, [accessLevel, dispatch]);

	const handleCreate = () => {
		console.log({
			selectedEntities,
			accessLevel,
			userId: user,
		});
		dispatch(
			triggerAddAccessLevel({
				selectedEntities,
				accessLevel,
				userId: user,
			})
		);

		setTimeout(() => {
			dispatch(
				dynamicGridSagaActions.refreshGridData({
					tableId: constants.TABLE_ID,
				})
			);
		}, 2000);

		onHide();
	};

	const handleSelectAccess = (selectedAccess: OptionSelection) => {
		const newselectedAccess = selectedAccess as OptionType;
		const newselectedAccessId = newselectedAccess ? newselectedAccess.value : '';
		setAccessLevel(newselectedAccessId);
	};

	const handleSelectUser = (selectedUser: OptionSelection) => {
		const newselectedUser = selectedUser as OptionType;
		const newselectedUserId = newselectedUser ? newselectedUser.value : '';
		setUser(newselectedUserId);
	};

	return (
		<ReactModal hideModal={onHide} height={500}>
			<ModalHeader>
				<Heading type="secondary">Create Row Access Controls</Heading>
			</ModalHeader>
			<ModalBody>
				<Stack isVertical gutter="small">
					<StackItem>
						<Label>User Email:</Label>
					</StackItem>
					<StackItem isStretch>
						<DropdownInner
							disabled={Boolean(rowAccessInfo?.UserID)}
							key="Users"
							selectedOption={selectedUserEmail}
							options={userOptions}
							isFullWidth
							updateHandler={handleSelectUser}
							isAutocomplete
							defaultValue={rowAccessInfo?.UserEmail}
						/>
					</StackItem>

					<StackItem>
						<Label>Access Level:</Label>
					</StackItem>
					<StackItem isStretch>
						<DropdownInner
							disabled={Boolean(rowAccessInfo?.AccessLevelID)}
							key="accessLevel"
							selectedOption={selectedAccessLevel}
							options={accessLevelsOptions}
							isFullWidth
							updateHandler={handleSelectAccess}
							isAutocomplete
							defaultValue={rowAccessInfo?.AccessLevelName}
						/>
					</StackItem>

					<StackItem>
						<Label>Access:</Label>
					</StackItem>
					<StackItem isStretch>
						<Select
							mode="multiple"
							onChange={(entities: string[]) => setSelectedEntities(entities)}
							defaultValue={selectedEntities}
							options={accessLevelEntities?.map((r: any) => ({
								value: r.AccessLevelEntityID,
								label: r.DisplayValue,
							}))}
						/>
					</StackItem>
				</Stack>
			</ModalBody>
			<ModalActions>
				<Stack align="right" gutter="small">
					<StackItem>
						<Button isText name="Cancel" handleClick={onHide} />
					</StackItem>

					<StackItem isStretch align="right">
						<Button
							isPrimary
							disabled={!user || !selectedEntities?.length || !accessLevel}
							name={rowAccessInfo?.AccessLevelID ? 'Update' : 'Create'}
							handleClick={handleCreate}
						/>
					</StackItem>
				</Stack>
			</ModalActions>
		</ReactModal>
	);
};
